// Renders any JSON deliverable content into elegant key/value blocks.
// Avoids self-recursive component references (which break react-refresh / babel).
import React from "react";

const HIDDEN_KEYS = new Set(["summary", "next_actions"]);

export default function DeliverableRender({ content }) {
  if (!content || content.error) {
    return <div className="text-sm text-rose-500">Error: {content?.error || "Unknown error"}</div>;
  }
  const entries = Object.entries(content).filter(([k]) => !HIDDEN_KEYS.has(k));
  return (
    <div className="space-y-5">
      {entries.map(([k, v]) => (
        <RenderField key={k} keyName={k} value={v} depth={0} />
      ))}
      {Array.isArray(content.next_actions) && content.next_actions.length > 0 && (
        <div className="border-t border-border pt-4 mt-4">
          <div className="text-overline text-primary mb-2">Next actions</div>
          <ul className="space-y-1.5">
            {content.next_actions.map((a, i) => (
              <li key={`na-${i}-${String(a).slice(0, 24)}`} className="text-sm flex items-start gap-2">
                <span className="text-primary">→</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Single non-recursive component — uses an iterative renderer to avoid self-reference.
function RenderField({ keyName, value, depth }) {
  return renderNode(keyName, value, depth);
}

// Iterative renderer — re-enters via `renderNode` calls but those go through the
// same exported helper, not back into a component named identically.
function renderNode(keyName, value, depth) {
  const label = humanize(keyName);
  if (value === null || value === undefined || value === "") return null;
  const t = typeof value;
  if (t === "string" || t === "number" || t === "boolean") {
    return (
      <div key={keyName}>
        <div className="text-overline text-muted-foreground mb-1">{label}</div>
        <div className="text-sm leading-relaxed">{String(value)}</div>
      </div>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const allPrim = value.every((it) => typeof it !== "object" || it === null);
    if (allPrim) {
      return (
        <div key={keyName}>
          <div className="text-overline text-muted-foreground mb-1.5">{label}</div>
          <ul className="space-y-1">
            {value.map((it, i) => (
              <li key={`${keyName}-${i}-${String(it).slice(0, 24)}`} className="text-sm">• {String(it)}</li>
            ))}
          </ul>
        </div>
      );
    }
    return renderTable(keyName, label, value);
  }
  // object — render as bordered group; cap depth to prevent runaway
  if (depth > 4) return null;
  return (
    <div key={keyName}>
      <div className="text-overline text-muted-foreground mb-2">{label}</div>
      <div className="border border-border rounded-md p-3.5 space-y-2.5">
        {Object.entries(value).map(([kk, vv]) => renderNode(kk, vv, depth + 1))}
      </div>
    </div>
  );
}

function renderTable(keyName, label, rows) {
  const cols = Array.from(new Set(rows.flatMap((it) => Object.keys(it || {}))));
  return (
    <div key={keyName}>
      <div className="text-overline text-muted-foreground mb-2">{label}</div>
      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-secondary">
            <tr>
              {cols.map((c) => (
                <th key={c} className="text-left p-2.5 font-semibold text-muted-foreground">{c.replace(/_/g, " ")}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${keyName}-row-${i}`} className="border-t border-border">
                {cols.map((c) => <td key={c} className="p-2.5 align-top">{fmtCell(row?.[c])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmtCell(v) {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function humanize(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
